/* eslint-disable max-lines-per-function */
/* eslint-disable camelcase */
import {DEFAULT_PAGE_SIZE} from './constants';

/**
 * @param {import('@constructor-io/constructorio-client-javascript').Facet} facet
 * @returns {import('custom-extensions/search/lib/api/types').SearchResponseRangeFacet | import('custom-extensions/search/lib/api/types').SearchResponseValueFacet}
 */
const transformFacet = facet => {
	const isRange = facet.type === 'range';

	return {
		collapse: false,
		facet_active: isRange
			? Boolean(facet.status && (typeof facet.status.min !== 'undefined' || typeof facet.status.max !== 'undefined'))
			: facet.options?.some(option => option.status === 'selected') || false,
		field: facet.name,
		label: facet.display_name,
		selection: isRange || facet.type === 'single' ? 'single' : 'multiple',
		type: isRange ? 'range' : 'list',
		...isRange
			? {
				high: facet.max,
				low: facet.min
			}
			: {
				values: (facet.options || []).map(option => {
					return {
						active: option.status === 'selected',
						count: option.count,
						label: option.display_name,
						type: 'value',
						value: option.value
					};
				})
			}
	};
};

/**
 * Build the canonical `filterSummary` (the "currently applied filters"
 * list `SearchApp.vue`/facet components render as removable chips) from
 * whichever facets/options are selected.
 *
 * @param {import('@constructor-io/constructorio-client-javascript').Facet[]} facets
 * @returns {import('custom-extensions/search/lib/api/types').SearchResponseFilterSummary[]}
 */
const transformFilterSummary = facets => {
	return facets.flatMap(facet => {
		const hasActiveRange = facet.status && (typeof facet.status.min !== 'undefined' || typeof facet.status.max !== 'undefined');

		if (facet.type === 'range' && hasActiveRange) {
			const range = [
				facet.status.min,
				facet.status.max
			];

			return [
				{
					field: facet.name,
					filterLabel: facet.display_name,
					filterValue: range.join('-'),
					label: `${facet.display_name}: ${range.join(' - ')}`,
					value: {
						rangeHigh: facet.status.max,
						rangeLow: facet.status.min
					}
				}
			];
		}

		return (facet.options || [])
			.filter(option => option.status === 'selected')
			.map(option => {
				return {
					field: facet.name,
					filterLabel: facet.display_name,
					filterValue: option.value,
					label: `${facet.display_name}: ${option.display_name}`,
					value: option.value
				};
			});
	});
};

/**
 * Group Constructor's `refined_content` items into the
 * `merchandising.content[placement]` shape (arrays of
 * `SearchResponseMerchandisingItem`) `Banner.vue` consumes
 *
 * `placement` is deliberately distinct from `position` -- a `placement`
 * of `inline` carries a `position` too, the numeric grid index
 * `insertInlineBanners()` (middleware.js) uses to splice it into the
 * results array.
 *
 * @param {Record<string, any>[]} refinedContent Raw `response.refined_content`
 * @returns {Record<string, import('custom-extensions/search/lib/api/types').SearchResponseMerchandisingItem[]>}
 */
const transformMerchandising = refinedContent => {
	return (refinedContent || []).reduce((content, item) => {
		const {placement, position, altText, assetUrl, body, ctaLink, ctaText, header, mobileAssetAltText, mobileAssetUrl} = item.data || {};
		const hasContent = Boolean(body || assetUrl);

		if (!placement || !hasContent) {
			return content;
		}

		return {
			...content,
			[placement]: [
				...content[placement] || [],
				{
					altText,
					assetUrl,
					body,
					ctaLink,
					ctaText,
					header,
					mobileAssetAltText,
					mobileAssetUrl,
					...position === void 0 ? {} : {position: Number(position)}
				}
			]
		};
	}, {});
};

/**
 * Convert Constructor's raw JSON response object into this app's canonical
 * `SearchResponse` interface (`custom-extensions/search/lib/api/types`).
 *
 * @param {import('@constructor-io/constructorio-client-javascript').SearchResponseType} response Raw Constructor `response` object
 * @param {import('./transform-search-request').TransformedSearchRequest} transformedRequest The request envelope this response answers, from `transformSearchRequest`
 * @returns {import('custom-extensions/search/lib/api/types').SearchResponse}
 */
export const transformSearchResponse = (response, transformedRequest, resultId, rawRequest) => {
	if (typeof response !== 'object' || response === null) {
		return {};
	}

	const {page = 1, resultsPerPage = DEFAULT_PAGE_SIZE} = transformedRequest.parameters || {};
	const totalResults = response.total_num_results || 0;
	const totalPages = Math.ceil(totalResults / resultsPerPage) || 1;
	const startingResultsIndex = (page - 1) * resultsPerPage;
	const facets = response.facets || [];
	const resultSources = response.result_sources || {};
	const totalMatches = (resultSources.token_match?.count || 0) + (resultSources.embeddings_match?.count || 0) || totalResults;

	return {
		cnstrcRequest: rawRequest,
		facets: facets.map(transformFacet),
		filterSummary: transformFilterSummary(facets),
		merchandising: {
			content: transformMerchandising(response.refined_content)
		},
		pagination: {
			begin: startingResultsIndex + 1,
			currentPage: page,
			defaultPerPage: DEFAULT_PAGE_SIZE,
			end: Math.min(totalResults, startingResultsIndex + (response.results?.length || 0)),
			isTotalMatchesCapped: totalMatches > totalResults,
			nextPage: Math.min(page + 1, totalPages),
			perPage: resultsPerPage,
			previousPage: Math.max(page - 1, 1),
			totalMatches,
			totalPages,
			totalResults
		},
		query: transformedRequest.term || '',
		resultId,
		resultLayout: 'grid',
		results: (response.results || []).map(result => {
			const data = result.data || {};

			return {
				...data,
				hover_image: data.hover_image_url,
				id: data.id,
				image: data.image_url,
				name: result.value,
				price: data.price,
				sku: data.sku,
				uid: data.id,
				url: data.url
			};
		}),
		sorting: {
			options: (response.sort_options || []).map(sortOption => {
				return {
					active: sortOption.status === 'selected',
					direction: sortOption.sort_order === 'ascending' ? 'asc' : 'desc',
					field: sortOption.sort_by,
					label: sortOption.display_name
				};
			})
		}
	};
};
